import { showErrorToast, useNosIdMutation } from '@/hooks/api/base.hooks';
import { api } from '@/portal-api.definition';
import {
  ActionType,
  appleLoginProvider,
  ErrorCodes,
  type ExternalLoginProviders,
  facebookLoginProvider,
  googleLoginProvider,
  type NOSIDError,
  useI18n,
  useToast,
} from '@nosinovacao/nosid-mfe-common';
import { useQueryClient } from '@tanstack/react-query';
import { AxiosError, AxiosHeaders } from 'axios';
import { loginOptionsQueryOptions } from './login-options.hook';

const providerLoginActions: Record<ExternalLoginProviders, () => Promise<string>> = {
  Apple: () => appleLoginProvider.login().then((res) => res.authorization.id_token),
  Google: () => googleLoginProvider.login().then((res) => res?.access_token ?? ''),
  Facebook: () => facebookLoginProvider.login().then((res) => res.authResponse?.accessToken ?? ''),
};

const providerLoginErrors: Record<
  ExternalLoginProviders,
  { alreadyRegistered: ErrorCodes; generic: ErrorCodes }
> = {
  Apple: {
    alreadyRegistered: ErrorCodes.AppleExternalProviderAssociationAlreadyRegistered,
    generic: ErrorCodes.AppleExternalProviderAssociationError,
  },
  Google: {
    alreadyRegistered: ErrorCodes.GoogleExternalProviderAssociationAlreadyRegistered,
    generic: ErrorCodes.GoogleExternalProviderAssociationError,
  },
  Facebook: {
    alreadyRegistered: ErrorCodes.FacebookExternalProviderAssociationAlreadyRegistered,
    generic: ErrorCodes.FacebookExternalProviderAssociationError,
  },
};

const providerAssociationSuccessMessages: Record<ExternalLoginProviders, string> = {
  Apple: 'Common.SnackBar.AppleSocialNetworkAssociated',
  Google: 'Common.SnackBar.GoogleSocialNetworkAssociated',
  Facebook: 'Common.SnackBar.FacebookSocialNetworkAssociated',
};

export function useAssociateSocialNetwork() {
  const toast = useToast();
  const { t } = useI18n();
  const queryClient = useQueryClient();
  return useNosIdMutation({
    ignoreSnackbarForErrors: ['*'],
    mutationFn: async (provider: ExternalLoginProviders) => {
      const token = await providerLoginActions[provider]().catch(() => '');
      if (!token) {
        throw new AxiosError(undefined, '400', undefined, undefined, {
          status: 400,
          statusText: 'Bad Request',
          headers: {},
          config: { headers: new AxiosHeaders() },
          data: undefined,
        });
      }
      return api.post(
        '/api/v1/social-networks',
        {},
        {
          headers: {
            'Social-Network-Authorization': `${provider} ${token}`,
          },
        },
      );
    },
    errorLogAction: (provider) => ({ ActionType: ActionType.AddSocialNetwork, Option: provider }),
    successLogAction: (provider) => ({ ActionType: ActionType.AddSocialNetwork, Option: provider }),
    onError: (err: AxiosError, provider) => {
      const bodyError = err.response?.data as NOSIDError | undefined;
      const providerErrorCodes = providerLoginErrors[provider];
      const errorCode =
        bodyError?.ErrorCode === ErrorCodes.ExternalProviderAssociationAlreadyRegistered
          ? providerErrorCodes.alreadyRegistered
          : providerErrorCodes.generic;
      showErrorToast(errorCode, toast, t, 'message');
    },
    onSuccess: (_res, provider) => {
      queryClient.setQueryData(loginOptionsQueryOptions.queryKey, (old) => {
        if (!old) return old;
        return { ...old, SocialNetworks: [...old.SocialNetworks, { Provider: provider }] };
      });
      toast.show('success', t(providerAssociationSuccessMessages[provider]));
    },
  });
}

const providerRemoveAssociationSuccessMessages: Record<ExternalLoginProviders, string> = {
  Apple: 'Common.SnackBar.AppleSocialNetworkRemoved',
  Google: 'Common.SnackBar.GoogleSocialNetworkRemoved',
  Facebook: 'Common.SnackBar.FacebookSocialNetworkRemoved',
};

const providerRemoveAssociationError: Record<ExternalLoginProviders, ErrorCodes> = {
  Apple: ErrorCodes.AppleExternalProviderRemoveError,
  Google: ErrorCodes.GoogleExternalProviderRemoveError,
  Facebook: ErrorCodes.FacebookExternalProviderRemoveError,
};

const providerLogoutActions: Record<ExternalLoginProviders, () => Promise<void>> = {
  Apple: () => appleLoginProvider.logout(),
  Google: () => googleLoginProvider.logout(),
  Facebook: () => facebookLoginProvider.logout(),
};

export function useRemoveSocialNetwork() {
  const toast = useToast();
  const { t } = useI18n();
  const queryClient = useQueryClient();
  return useNosIdMutation({
    ignoreSnackbarForErrors: ['*'],
    mutationFn: async (provider: ExternalLoginProviders) => {
      await providerLogoutActions[provider]().catch(() => {});
      return api.delete('/api/v1/social-networks', { search: { authProvider: provider } });
    },
    errorLogAction: (provider) => ({
      ActionType: ActionType.RemoveSocialNetwork,
      Option: provider,
    }),
    successLogAction: (provider) => ({
      ActionType: ActionType.RemoveSocialNetwork,
      Option: provider,
    }),
    onError: (_, provider) => {
      showErrorToast(providerRemoveAssociationError[provider], toast, t, 'message');
    },
    onSuccess: (_res, provider) => {
      queryClient.setQueryData(loginOptionsQueryOptions.queryKey, (old) => {
        if (!old) return old;
        return {
          ...old,
          SocialNetworks: old.SocialNetworks.filter((sn) => sn.Provider !== provider),
        };
      });
      toast.show('success', t(providerRemoveAssociationSuccessMessages[provider]));
    },
  });
}
